import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  Form,
  InputNumber,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import cn from 'classnames';

import { ConfirmDeletingElement } from '@app/components/ConfirmDeletingElement';
import {
  type CertificateSaleOption,
  useCertificateSaleItems,
} from '@app/components/RecordModal/components/RecordDetails/components/CertificateSaleItems/hooks';
import type {
  BranchUserOption,
  CertificateSaleDraftItem,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { NumberInputWithMask } from '@app/components/RecordModal/components/RecordDetails/ui/NumberInputWithMask';
import { WithTooltip } from '@app/components/WithTooltip';
import { divideAmount, withTestId } from '@app/utils';

import type { FC } from 'react';

import './CertificateSaleItems.scss';

const { Item } = Form;
const { Text } = Typography;

const SELECT_CERTIFICATE_FIRST = 'Сначала выберите сертификат';
const QUANTITY_HINT =
  'Один проданный сертификат — один уникальный номер. Для второго сертификата добавьте ещё одну строку';
const PAID_CERTIFICATE_HINT = 'Сертификат оплачен: изменить и удалить нельзя';
const SOLD_CERTIFICATE_HINT = 'Сертификат продан: можно изменить только скидку';
const DELETE_CERTIFICATE_LABEL = 'Удалить сертификат';

interface CertificateSaleItemsProps {
  branchUserOptions: BranchUserOption[];
  activeBranchId: number | null;
  /** Значение поля формы `certificates` (передаёт Form.Item) */
  items?: CertificateSaleDraftItem[];
  onChange?(items: CertificateSaleDraftItem[]): void;
  disabled?: boolean;
}

/**
 * Блок «Сертификаты» в «Деталях записи»: продажа сертификата через запись.
 * Устроен как блок абонементов (SubscriptionSaleItems) и живёт в форме
 * записи полем `certificates`.
 */
export const CertificateSaleItems: FC<CertificateSaleItemsProps> = ({
  branchUserOptions,
  activeBranchId,
  items = [],
  onChange,
  disabled,
}) => {
  const {
    rows,
    certificateOptions,
    isCertificatesLoading,
    totalQuantity,
    totalAmount,
    addItemHandler,
    deleteItemHandler,
    isDeleteConfirmOpen,
    deleteConfirmText,
    confirmDeleteHandler,
    deleteModalOpenChangeHandler,
    certificateChangeHandler,
    sellerChangeHandler,
    priceChangeHandler,
    discountCurrencyChangeHandler,
    discountPercentChangeHandler,
  } = useCertificateSaleItems({ items, onChange, activeBranchId });

  const addButton = (
    <Button
      type="primary"
      className="CertificateSaleItems__add-button"
      onClick={() => void addItemHandler()}
      disabled={disabled}
      data-testid="add-certificate-sale-item"
    >
      Добавить сертификат
    </Button>
  );

  if (rows.length === 0) {
    return <div className="CertificateSaleItems">{addButton}</div>;
  }

  return (
    <div className="CertificateSaleItems">
      <div className="CertificateSaleItems__scroll-wrapper">
        <div className="CertificateSaleItems__items">
          <div className="CertificateSaleItems__header">
            <Text strong>Название</Text>
            <Text strong className="CertificateSaleItems__header-center">
              Номинал
            </Text>
            <Text strong>Продавец</Text>
            <Text strong>Кол-во</Text>
            <Text strong>Цена</Text>
            <Text strong>Скидка</Text>
            <Text strong className="CertificateSaleItems__header-center">
              К оплате
            </Text>
            <div />
          </div>
          {rows.map(({ item, total, isSold, isPaid, isEditable }) => {
            const isCertificateSelected = Boolean(item.certificateId);
            const isSoldUnpaid = isSold && !isPaid;
            // Продавец и цена проданного сертификата не меняются, скидка —
            // пока сертификат не оплачен.
            const isSaleFieldDisabled =
              disabled || isSold || !isCertificateSelected;
            const isDiscountDisabled =
              disabled || !isEditable || !isCertificateSelected;
            const saleFieldHint = isSoldUnpaid
              ? SOLD_CERTIFICATE_HINT
              : SELECT_CERTIFICATE_FIRST;

            return (
              <div
                className={cn('CertificateSaleItems__item', {
                  'CertificateSaleItems__item--sold': isSold,
                })}
                key={item.id}
              >
                <Item
                  label="Название"
                  className="CertificateSaleItems__field CertificateSaleItems__field--name"
                >
                  <WithTooltip
                    show={isSoldUnpaid}
                    title={SOLD_CERTIFICATE_HINT}
                  >
                    <Select<number, CertificateSaleOption>
                      showSearch
                      optionFilterProp="label"
                      placeholder="Выберите сертификат"
                      options={certificateOptions}
                      value={item.certificateId}
                      disabled={disabled || isSold}
                      loading={isCertificatesLoading}
                      data-testid="certificate-sale-certificate"
                      onChange={(value) =>
                        certificateChangeHandler(item.id, value)
                      }
                      labelRender={() => item.name || 'Сертификат без названия'}
                      optionRender={({ data }) => (
                        <div className="CertificateSaleItems__option">
                          <Text strong>
                            {data.label || 'Сертификат без названия'}
                          </Text>
                          <span>{divideAmount(data.nominal)}</span>
                        </div>
                      )}
                      notFoundContent={
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Сертификаты не найдены. Новый сертификат создаётся только в реестре сертификатов"
                        />
                      }
                    />
                  </WithTooltip>
                  {isSold && (
                    <Text
                      type="secondary"
                      className="CertificateSaleItems__status"
                      data-testid="certificate-sale-status"
                    >
                      {isPaid ? (
                        <>
                          <LockOutlined /> Оплачен
                        </>
                      ) : (
                        'Продан'
                      )}
                    </Text>
                  )}
                </Item>
                <div className="CertificateSaleItems__nominal">
                  <Text strong className="CertificateSaleItems__nominal-label">
                    Номинал
                  </Text>
                  <Text data-testid="certificate-sale-nominal">
                    {divideAmount(item.nominal)} ₽
                  </Text>
                </div>
                <WithTooltip
                  show={!isCertificateSelected || isSoldUnpaid}
                  title={saleFieldHint}
                >
                  <Item
                    label="Продавец"
                    className="CertificateSaleItems__field"
                  >
                    <Select
                      options={branchUserOptions}
                      value={item.sellerId}
                      disabled={isSaleFieldDisabled}
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      data-testid="certificate-sale-seller"
                      onChange={(value) => sellerChangeHandler(item.id, value)}
                      popupRender={withTestId(
                        'certificate-sale-seller-dropdown'
                      )}
                    />
                  </Item>
                </WithTooltip>
                <Tooltip title={QUANTITY_HINT}>
                  <Item label="Кол-во" className="CertificateSaleItems__field">
                    <InputNumber
                      value={1}
                      disabled
                      data-testid="certificate-sale-quantity"
                    />
                  </Item>
                </Tooltip>
                <WithTooltip
                  show={!isCertificateSelected || isSoldUnpaid}
                  title={saleFieldHint}
                >
                  <Item label="Цена" className="CertificateSaleItems__field">
                    <NumberInputWithMask
                      value={item.price}
                      disabled={isSaleFieldDisabled}
                      dataTestId="certificate-sale-price"
                      onChange={(value) => priceChangeHandler(item.id, value)}
                    />
                  </Item>
                </WithTooltip>
                <WithTooltip
                  show={!isCertificateSelected}
                  title={SELECT_CERTIFICATE_FIRST}
                >
                  <Item
                    label="Скидка"
                    className="CertificateSaleItems__field CertificateSaleItems__field--discount"
                  >
                    <NumberInputWithMask
                      value={item.discountCurrency}
                      disabled={isDiscountDisabled}
                      dataTestId="certificate-sale-discount-price"
                      onChange={(value) =>
                        discountCurrencyChangeHandler(item.id, value)
                      }
                    />
                    <NumberInputWithMask
                      value={item.discountPercent}
                      disabled={isDiscountDisabled}
                      dataTestId="certificate-sale-discount-percent"
                      suffix="%"
                      onChange={(value) =>
                        discountPercentChangeHandler(item.id, value)
                      }
                    />
                  </Item>
                </WithTooltip>
                <div className="CertificateSaleItems__total">
                  <Text strong className="CertificateSaleItems__total-label">
                    К оплате
                  </Text>
                  <Text data-testid="certificate-sale-total">
                    {divideAmount(total)}
                  </Text>
                </div>
                {isPaid ? (
                  <Tooltip title={PAID_CERTIFICATE_HINT}>
                    <LockOutlined
                      className="CertificateSaleItems__lock"
                      data-testid="certificate-sale-lock"
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={DELETE_CERTIFICATE_LABEL}>
                    <Button
                      icon={<DeleteOutlined />}
                      aria-label={DELETE_CERTIFICATE_LABEL}
                      onClick={() => deleteItemHandler(item.id)}
                      disabled={disabled}
                      data-testid="delete-certificate-sale-item"
                    />
                  </Tooltip>
                )}
              </div>
            );
          })}
          <div className="CertificateSaleItems__item CertificateSaleItems__item--summary">
            <Text strong>Итого:</Text>
            <div />
            <div />
            <Text className="CertificateSaleItems__summary-quantity">
              {divideAmount(totalQuantity)} шт
            </Text>
            <div />
            <div />
            <div className="CertificateSaleItems__total">
              <Text data-testid="certificate-sale-summary-total">
                {divideAmount(totalAmount)}
              </Text>
            </div>
            <div />
          </div>
        </div>
      </div>
      <div className="CertificateSaleItems__footer">{addButton}</div>
      <ConfirmDeletingElement
        modalOpen={isDeleteConfirmOpen}
        setModalOpen={deleteModalOpenChangeHandler}
        handleDelete={confirmDeleteHandler}
        title="Удаление проданного сертификата"
        content={deleteConfirmText}
        modalDataTestId="certificate-sale-delete-confirm"
      />
    </div>
  );
};
